import type {
  ChartDrawingAnchor,
  ChartDrawingItem,
  ChartDrawingKind,
  ChartDrawingStyle,
  ChartDrawingTool,
} from '../types';
import type { ProviderName } from './providerRouting';

export const DRAWING_TOOL_ORDER: ChartDrawingTool[] = [
  'cursor',
  'trendLine',
  'ray',
  'extendedLine',
  'horizontalLine',
  'horizontalRay',
  'rectangle',
  'parallelChannel',
  'fibRetracement',
  'text',
];

export const FIB_RETRACEMENT_LEVELS = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1] as const;

export function getDrawingToolLabel(tool: ChartDrawingTool): string {
  switch (tool) {
    case 'cursor':
      return 'Cursor';
    case 'horizontalLine':
      return 'Horizontal line';
    case 'horizontalRay':
      return 'Horizontal ray';
    case 'trendLine':
      return 'Trend line';
    case 'ray':
      return 'Ray';
    case 'extendedLine':
      return 'Extended line';
    case 'rectangle':
      return 'Rectangle';
    case 'parallelChannel':
      return 'Parallel channel';
    case 'fibRetracement':
      return 'Fib retracement';
    case 'text':
      return 'Text';
  }
}

export function getRequiredAnchorCount(kind: ChartDrawingKind): number {
  switch (kind) {
    case 'horizontalLine':
    case 'horizontalRay':
    case 'text':
      return 1;
    case 'parallelChannel':
      return 3;
    default:
      return 2;
  }
}

export function createDefaultDrawingStyle(kind: ChartDrawingKind): ChartDrawingStyle {
  if (kind === 'rectangle' || kind === 'parallelChannel' || kind === 'fibRetracement') {
    return {
      strokeColor: '#7dd3fc',
      strokeWidth: 2,
      lineStyle: 'solid',
      fillColor: 'rgba(14, 165, 233, 0.12)',
      textColor: '#e2e8f0',
      textSize: 12,
    };
  }

  if (kind === 'text') {
    return {
      strokeColor: '#f8fafc',
      strokeWidth: 2,
      lineStyle: 'solid',
      fillColor: null,
      textColor: '#f8fafc',
      textSize: 14,
    };
  }

  return {
    strokeColor: '#7dd3fc',
    strokeWidth: 2,
    lineStyle: 'solid',
    fillColor: null,
    textColor: '#e2e8f0',
    textSize: 12,
  };
}

export function createDrawingItem(params: {
  provider: ProviderName;
  symbol: string;
  kind: ChartDrawingKind;
  anchors: ChartDrawingAnchor[];
  text?: string | null;
}): ChartDrawingItem {
  const now = new Date().toISOString();
  return {
    id: `drawing-${Math.random().toString(36).slice(2, 10)}`,
    provider: params.provider,
    symbol: params.symbol,
    kind: params.kind,
    anchors: params.anchors,
    visible: true,
    locked: false,
    text: params.text?.trim() ? params.text.trim() : null,
    createdAt: now,
    updatedAt: now,
    style: createDefaultDrawingStyle(params.kind),
  };
}

export function updateDrawingAnchors(
  drawing: ChartDrawingItem,
  anchors: ChartDrawingAnchor[],
): ChartDrawingItem {
  return {
    ...drawing,
    anchors,
    updatedAt: new Date().toISOString(),
  };
}

export function buildFibPriceLevels(firstPrice: number, secondPrice: number): Array<{ ratio: number; price: number }> {
  const delta = secondPrice - firstPrice;
  return FIB_RETRACEMENT_LEVELS.map((ratio) => ({
    ratio,
    price: secondPrice - delta * ratio,
  }));
}
